import React, { RefObject, useState } from 'react';
import Button from 'components/utils/Button';
import { Paperclip, X, LoaderCircle, BookOpen } from "lucide-react";
import sessionAPI from 'apis/sessionAPI';
import readmeAPI from 'apis/readmeAPI';
import { cn } from 'libs/classNameUtils';

const CHARACTER_LIMIT = 5000;

type ChatInputProps = {
  inputValue: string;
  setInputValue: (value: string) => void;
  textAreaFocus: boolean;
  setTextAreaFocus: (focus: boolean) => void;
  blockInput: boolean;
  handleSubmit: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleAttachClick: () => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedFiles: File[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  fileInputRef: RefObject<HTMLInputElement | null>;
  handleRemoveFile: (fileToRemove: File) => void;
  uploading?: boolean;
};

const ChatInput: React.FC<ChatInputProps> = ({
  inputValue,
  setInputValue,
  textAreaFocus,
  setTextAreaFocus,
  blockInput,
  handleSubmit,
  handleInputChange,
  handleKeyDown,
  handleAttachClick,
  handleFileInputChange,
  selectedFiles,
  setSelectedFiles,
  textareaRef,
  fileInputRef,
  handleRemoveFile,
  uploading = false
}) => {
  const isSendDisabled = inputValue.length > CHARACTER_LIMIT;
  const [loadingReadme, setLoadingReadme] = useState(false);

  const handleReadmeClick = async () => {
    try {
      setLoadingReadme(true);
      const { data, statusCode } = await readmeAPI.getReadmeUrl();
      
      if (statusCode === 200 && data && data.url) {
        window.open(data.url, '_blank');
      } else {
        console.error('README URL is missing or invalid in the response');
      }
    } catch (error) {
      console.error('Error fetching README URL:', error);
    } finally {
      setLoadingReadme(false);
    }
  };

  const getBorderStyle = () => {
    if (isSendDisabled) return 'border-red-500 border-2';
    if (textAreaFocus) return 'border-BAMPink border-2';
    return 'border-BALGray';
  };

  const getButtonStyles = () => {
    if (blockInput) {
      return {
        bgColor: 'bg-BALGray cursor-not-allowed',
        fontColor: 'text-BADGray',
        hoverBgColor: 'bg-BALGray',
        hoverBorderColor: 'border-BALGray',
        borderColor: 'border-BALGray',
      };
    }

    if (isSendDisabled) {
      return {
        bgColor: 'bg-red-700',
        fontColor: 'text-BALGray',
        hoverBgColor: 'bg-red-700',
        hoverBorderColor: 'border-red-700',
        borderColor: 'border-red-700',
      };
    }

    return {
      bgColor: '',
      fontColor: 'text-BADBlue',
      hoverBgColor: 'bg-BAMPink',
      hoverBorderColor: 'border-BAMPink',
      borderColor: 'border-BADBlue',
    };
  };

  const buttonStyles = getButtonStyles();

  return (
    <div>
      <div className="px-8 text-BAMGray text-sm md:text-base">
        <div>
          Note: We currently support text, images (JPEG, PNG), and PDF files. For PDFs, the file size is limited to 5 pages per document. You can upload a maximum of 3 files per message.
        </div>
        <div className="mt-2 flex">
          <button
            onClick={handleReadmeClick}
            disabled={loadingReadme}
            className={cn(`flex items-center text-BADBlue hover:text-BAMPink`, loadingReadme && 'opacity-50 cursor-not-allowed')}
          >
            {loadingReadme ? (
              <LoaderCircle className="animate-spin mr-1" size={16} />
            ) : (
              <BookOpen className="mr-1" size={16} />
            )}
            <span>README</span>
          </button>
        </div>
      </div>
      <div className="flex px-8 py-4 gap-2.5 flex-row">
        <div className={cn(`border rounded-[20px] flex flex-col w-full`, getBorderStyle())}>
          <textarea
            ref={textareaRef}
            className={cn(
              `p-2.5 rounded-[24px] resize-none scrollbar-thin scrollbar-track-transparent scrollbar-thumb-BAMPink border-transparent focus:outline-none`,
              blockInput ? 'bg-BALGray cursor-not-allowed' : 'text-BADBlue'
            )}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setTextAreaFocus(true)}
            onBlur={() => setTextAreaFocus(false)}
            disabled={blockInput}
            placeholder={blockInput ? '' : 'Type your message here...'}
          />
          <div className="flex items-center p-2 flex-wrap gap-2">
            <button 
              type="button"
              onClick={handleAttachClick}
              className={cn(
                `h-[32px] sm:h-[40px] px-2 sm:px-3 border rounded-[24px] flex items-center justify-center gap-1 whitespace-nowrap text-xs sm:text-sm`,
                blockInput || uploading
                  ? 'text-BADGray bg-BALGray border-BALGray cursor-not-allowed'
                  : 'text-BADBlue border-BADBlue hover:bg-BAMPink hover:border-BAMPink hover:text-BAWhite'
              )}
              disabled={blockInput || uploading}
            >
              <Paperclip size={16} />
              <span>Attach</span>
            </button>
            <input 
              ref={fileInputRef}
              type="file" 
              id="file-upload" 
              multiple
              className="hidden" 
              onChange={handleFileInputChange}
            />
            <div className="flex flex-wrap gap-2 max-w-full">
              {selectedFiles.map((file, index) => (
                <div 
                  key={`${file.name}-${index}`} 
                  className="bg-BALGray/30 text-BADBlue px-2 py-1 rounded-full flex items-center text-sm whitespace-nowrap"
                >
                  <span className="truncate max-w-[150px]" title={file.name}>
                    {file.name}
                  </span>
                  {uploading ? (
                    <LoaderCircle className="ml-1 animate-spin text-BADBlue" size={14} />
                  ) : (
                    <button 
                      onClick={() => handleRemoveFile(file)}
                      className="ml-1 text-BADBlue hover:text-BAMPink"
                      title="Remove file"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div
          className="flex items-center justify-center"
          onClick={isSendDisabled || blockInput ? undefined : handleSubmit}
        >
          <Button
            text="Send"
            width="w-[50px] sm:w-[60px]"
            height="h-[32px] sm:h-[40px]"
            radius={24}
            fontSize="text-xs sm:text-sm"
            {...buttonStyles}
          />
        </div>
      </div>
      <div
        className={cn(
          `px-8 mb-2 text-sm`,
          isSendDisabled ? 'text-red-500 font-bold' : 'text-BAMGray'
        )}
      >
        {inputValue.length} / {CHARACTER_LIMIT} characters
      </div>
    </div>
  );
};

export default ChatInput;